import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import { Calendar, ChevronLeft, ChevronRight, Clock, User, CheckCircle2, XCircle } from 'lucide-react'
import { formatDate, formatTime } from '../utils/helpers'

const TherapyCalendar = ({ therapies, onTherapyUpdate, onTherapyClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [draggedTherapy, setDraggedTherapy] = useState(null)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
  const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
  const startDate = new Date(monthStart)
  startDate.setDate(startDate.getDate() - startDate.getDay()) // Start from Sunday

  const days = []
  const currentDay = new Date(startDate)
  for (let i = 0; i < 42; i++) {
    days.push(new Date(currentDay))
    currentDay.setDate(currentDay.getDate() + 1)
  }

  const therapiesByDate = useMemo(() => {
    const grouped = {}
    therapies.forEach((therapy) => {
      const date = new Date(therapy.scheduled_date).toDateString()
      if (!grouped[date]) grouped[date] = []
      grouped[date].push(therapy)
    })
    return grouped
  }, [therapies])

  const isSameMonth = (date) => {
    return date.getMonth() === currentDate.getMonth()
  }

  const isToday = (date) => {
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  const handleDragStart = (e, therapy) => {
    setDraggedTherapy(therapy)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = async (e, targetDate) => {
    e.preventDefault()
    if (!draggedTherapy || !canWrite('therapies')) return

    try {
      const newDate = new Date(targetDate)
      newDate.setHours(parseInt(draggedTherapy.time_of_day === 'Morning' ? 9 : draggedTherapy.time_of_day === 'Afternoon' ? 14 : 19))

      const { error } = await supabase
        .from('therapies')
        .update({ scheduled_date: newDate.toISOString() })
        .eq('id', draggedTherapy.id)
        .eq('organization_id', organizationId)

      if (error) throw error

      if (onTherapyUpdate) {
        onTherapyUpdate()
      }
    } catch (error) {
      console.error('Error updating therapy date:', error)
      alert('Failed to update therapy date')
    } finally {
      setDraggedTherapy(null)
    }
  }

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'missed':
        return 'bg-red-100 text-red-700 border-red-200'
      default:
        return 'bg-blue-100 text-blue-700 border-blue-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={12} />
      case 'missed':
        return <XCircle size={12} />
      default:
        return <Clock size={12} />
    }
  }

  return (
    <Card variant="glass" className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={prevMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <h3 className="text-xl font-bold text-gray-900">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={nextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setCurrentDate(new Date())}
        >
          Today
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day Headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="p-2 text-center text-sm font-semibold text-gray-600">
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {days.map((day, idx) => {
          const dayKey = day.toDateString()
          const dayTherapies = therapiesByDate[dayKey] || []
          const isCurrentMonth = isSameMonth(day)
          const isTodayDate = isToday(day)

          return (
            <div
              key={idx}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, day)}
              className={`min-h-24 p-1 border border-gray-200 rounded-lg ${
                isCurrentMonth ? 'bg-white' : 'bg-gray-50'
              } ${isTodayDate ? 'ring-2 ring-primary-500' : ''} ${
                canWrite('therapies') ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
            >
              <div
                className={`text-xs font-semibold mb-1 ${
                  isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                } ${isTodayDate ? 'text-primary-600' : ''}`}
              >
                {day.getDate()}
              </div>
              <div className="space-y-1 max-h-20 overflow-y-auto">
                {dayTherapies.map((therapy) => (
                  <motion.div
                    key={therapy.id}
                    draggable={canWrite('therapies')}
                    onDragStart={(e) => handleDragStart(e, therapy)}
                    onClick={() => onTherapyClick && onTherapyClick(therapy)}
                    className={`text-xs p-1.5 rounded border cursor-pointer transition-all ${
                      getStatusColor(therapy.status)
                    } ${canWrite('therapies') ? 'hover:shadow-md' : ''}`}
                    title={`${therapy.name} - ${therapy.clients?.first_name} ${therapy.clients?.last_name}`}
                  >
                    <div className="flex items-center gap-1">
                      {getStatusIcon(therapy.status)}
                      <span className="font-semibold truncate">{therapy.name}</span>
                    </div>
                    <div className="text-xs opacity-75 truncate">
                      {therapy.time_of_day} • {therapy.clients?.first_name}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200"></div>
          <span className="text-gray-600">Scheduled</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-4 h-4 rounded bg-green-100 border border-green-200"></div>
          <span className="text-gray-600">Completed</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-4 h-4 rounded bg-red-100 border border-red-200"></div>
          <span className="text-gray-600">Missed</span>
        </div>
        {canWrite('therapies') && (
          <div className="flex items-center gap-2 text-sm text-gray-500 ml-auto">
            <span>💡 Drag therapies to reschedule</span>
          </div>
        )}
      </div>
    </Card>
  )
}

export default TherapyCalendar

